import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle, ArrowRight, Building2, Shield, TrendingUp, Users, 
  BarChart3, Lock, Zap, Globe, HeadphonesIcon, FileText,
  Target, Clock, Award
} from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import TrustpilotWidget from "@/components/TrustpilotWidget";

const Enterprise = () => {
  const enterpriseCapabilities = [
    {
      icon: Building2,
      title: "Enterprise-Grade Infrastructure",
      description: "Scalable systems built to handle millions of interactions with 99.9% uptime SLA."
    },
    {
      icon: Shield,
      title: "Security & Compliance",
      description: "GDPR compliant, SOC 2 ready processes with enterprise-level data protection."
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics & Reporting",
      description: "Custom dashboards, attribution modeling, and executive-level reporting."
    },
    {
      icon: Users,
      title: "Dedicated Account Team",
      description: "Named account manager, growth strategist, and technical support team."
    },
    {
      icon: Lock,
      title: "Custom Integrations",
      description: "Seamless integration with your CRM, ERP, and existing tech stack."
    },
    {
      icon: Globe,
      title: "Multi-Market Expansion",
      description: "Localized campaigns across regions with cultural and linguistic expertise."
    }
  ];

  const b2bResults = [
    { metric: "Average Deal Size Increase", value: "340%", description: "Higher quality leads close at higher values" },
    { metric: "Sales Cycle Reduction", value: "45%", description: "Faster qualification and decision making" },
    { metric: "Pipeline Growth", value: "£2.4M+", description: "Average annual pipeline generated" },
    { metric: "Customer Acquisition Cost", value: "-62%", description: "Reduced through targeted positioning" }
  ];

  const useCases = [
    {
      title: "Account-Based Marketing",
      description: "Target specific high-value accounts with personalized multi-channel campaigns.",
      features: ["LinkedIn advertising", "Personalized landing pages", "Account scoring", "Sales enablement"]
    },
    {
      title: "Demand Generation",
      description: "Build predictable pipeline with content-led inbound and strategic outbound.",
      features: ["Content marketing", "Lead nurturing", "Marketing automation", "Conversion optimization"]
    },
    {
      title: "Market Expansion",
      description: "Enter new verticals or territories with data-driven market entry strategies.",
      features: ["Market research", "Competitor analysis", "Positioning strategy", "Go-to-market execution"]
    }
  ];

  const complianceBadges = [
    "GDPR Compliant",
    "ICO Registered",
    "ISO 27001 Aligned",
    "Enterprise SLA"
  ];

  return (
    <>
      <EnhancedSEO 
        title="Enterprise B2B Growth Solutions | Vincent Growth Partners"
        description="Enterprise-grade B2B marketing for scaling companies. Account-based marketing, demand generation, and market expansion with dedicated teams and guaranteed ROI."
        keywords="enterprise B2B marketing, account-based marketing, demand generation, B2B lead generation, enterprise marketing agency"
        url="/enterprise"
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="py-20 lg:py-28 bg-gradient-to-br from-primary via-primary/95 to-secondary/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-secondary/20 via-transparent to-transparent" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center gap-2 mb-6">
                {complianceBadges.map((badge, i) => (
                  <Badge key={i} variant="outline" className="bg-white/10 border-white/30 text-white text-xs">
                    {badge}
                  </Badge>
                ))}
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                B2B Growth Infrastructure
                <span className="block text-secondary">For Scaling Companies</span>
              </h1>
              
              <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
                Enterprise-grade marketing systems that generate predictable pipeline, reduce customer acquisition costs, and accelerate revenue growth. Built for B2B companies serious about scale.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
                <Button 
                  asChild 
                  size="xl"
                  className="bg-secondary hover:bg-secondary/90 text-primary font-semibold group"
                >
                  <a href="/do-you-qualify">
                    Schedule Strategic Review
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button 
                  asChild 
                  variant="outline" 
                  size="xl"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  <a href="/resources">
                    Download Enterprise Guide
                    <FileText className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-6 text-white/80 text-sm">
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  <span>£50M+ Pipeline Generated</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  <span>200+ B2B Clients</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  <span>340% Avg Deal Size Increase</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* B2B Results */}
        <section className="py-16 bg-secondary/5">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge variant="secondary" className="mb-4">Measurable Impact</Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Results That Matter to B2B Leaders
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  We focus on metrics that impact your bottom line, not vanity numbers.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {b2bResults.map((result, index) => (
                  <Card key={index} className="text-center border-border/50 hover:border-accent/50 transition-colors">
                    <CardHeader className="pb-2">
                      <div className="text-4xl font-bold bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent">
                        {result.value}
                      </div>
                      <CardTitle className="text-lg text-foreground">{result.metric}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>{result.description}</CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Enterprise Capabilities */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge variant="secondary" className="mb-4">Enterprise Capabilities</Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Built for Enterprise Requirements
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Infrastructure, security, and support that meets the demands of scaling B2B organizations.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {enterpriseCapabilities.map((capability, index) => {
                  const IconComponent = capability.icon;
                  return (
                    <Card key={index} className="border-border/50 hover:shadow-lg transition-all duration-300">
                      <CardHeader>
                        <div className="w-12 h-12 bg-gradient-to-br from-secondary to-accent rounded-lg flex items-center justify-center mb-4">
                          <IconComponent className="w-6 h-6 text-primary" />
                        </div>
                        <CardTitle className="text-xl text-foreground">{capability.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base">{capability.description}</CardDescription>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 bg-secondary/5">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge variant="secondary" className="mb-4">Solutions</Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Enterprise Use Cases
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Tailored strategies for your specific growth objectives.
                </p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {useCases.map((useCase, index) => (
                  <Card key={index} className="border-border/50 hover:border-accent/30 transition-colors">
                    <CardHeader>
                      <CardTitle className="text-xl text-foreground">{useCase.title}</CardTitle>
                      <CardDescription className="text-base">{useCase.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {useCase.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-muted-foreground">
                            <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Trustpilot Section */}
        <TrustpilotWidget />

        {/* Process Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <Badge variant="secondary" className="mb-4">Our Process</Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Enterprise Engagement Model
                </h2>
              </div>

              <div className="space-y-8">
                {[
                  { step: "01", title: "Strategic Discovery", description: "Deep-dive into your business model, competitive landscape, and growth objectives.", duration: "Week 1-2" },
                  { step: "02", title: "Infrastructure Setup", description: "Technical implementation, integrations, and campaign architecture.", duration: "Week 2-4" },
                  { step: "03", title: "Launch & Optimize", description: "Campaign activation with continuous A/B testing and optimization.", duration: "Week 4+" },
                  { step: "04", title: "Scale & Expand", description: "Proven channels scaled, new opportunities explored, ROI maximized.", duration: "Ongoing" }
                ].map((phase, index) => (
                  <div key={index} className="flex gap-6 items-start">
                    <div className="w-16 h-16 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-bold text-primary">{phase.step}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-foreground">{phase.title}</h3>
                        <Badge variant="outline" className="text-xs">{phase.duration}</Badge>
                      </div>
                      <p className="text-muted-foreground">{phase.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary to-secondary/80">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Build Your Growth Engine?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Schedule a strategic review with our enterprise team. We'll analyze your current position and outline a roadmap to predictable pipeline growth.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  asChild 
                  size="xl"
                  className="bg-secondary hover:bg-secondary/90 text-primary font-semibold group"
                >
                  <a href="/do-you-qualify">
                    Schedule Strategic Review
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button 
                  asChild 
                  variant="outline" 
                  size="xl"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  <a href="tel:+447857450763">
                    <HeadphonesIcon className="w-5 h-5 mr-2" />
                    Speak to Enterprise Team
                  </a>
                </Button>
              </div>

              <p className="text-white/70 text-sm mt-6">
                No commitment required • Confidential discussion • Typically responds within 24 hours
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Enterprise;
